import { Box, Dialog, IconButton, Typography, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import UserFormBody from "./UserFormBody";

// Popup modal for creating / editing a user (spec §5.4). Flat white dialog with
// a blue gradient header (the spec reserves gradients for modal headers) and the
// shared UserFormBody (credentials, role, permission matrix) below.
//
// Props:
//   open      — controls visibility.
//   onClose   — close without saving (backdrop / close button / Cancel).
//   onSuccess — called after a successful save (receives { isEdit }).
//   editId    — null = create; an id = edit.
function AddUserModal({ open, onClose, onSuccess, editId = null }) {
  const isEdit = editId != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            background: "#fff",
            border: `1px solid ${COLORS.hairline}`,
            boxShadow: "0 24px 64px rgba(16, 24, 40, 0.18)",
            backgroundImage: "none",
            overflow: "hidden",
          },
        },
      }}
    >
      {/* Gradient header */}
      <Box
        sx={{
          position: "relative",
          background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 60%, #60A5FA 100%)`,
          color: "#fff",
          px: { xs: 3, md: 4 },
          py: 3,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.9)",
            "&:hover": { background: "rgba(255,255,255,0.18)", color: "#fff" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ManageAccountsOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                opacity: 0.85,
              }}
            >
              {isEdit ? "Edit User" : "Create User"}
            </Typography>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: { xs: 20, md: 24 },
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.15,
              }}
            >
              {isEdit ? "Update account & access" : "Add a new account"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Form body — scrolls if taller than the viewport */}
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          maxHeight: { xs: "70vh", md: "72vh" },
          overflowY: "auto",
          background: COLORS.canvas,
        }}
      >
        <UserFormBody
          key={editId ?? "new"}
          userId={editId}
          onSuccess={onSuccess}
          onCancel={onClose}
        />
      </Box>
    </Dialog>
  );
}

export default AddUserModal;
